import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { apiRoot } from '../config'
import axios from 'axios'
import { updatePreferences } from './PreferencesSlice'
import { AuthState } from './AuthSlice'
import i18n from 'i18next'
import { queueOperation } from './VocabularyOperationQueueSlice'
import { ApiVocabulary, ApiVocabularyRow, IVocabulary, IVocabularyRow } from '../Types/VocabularyTypes'

export const updateRow = createAsyncThunk(
  'vocabularies/updateRow',
  async (row: Partial<IVocabularyRow>, { dispatch }) => {
    return dispatch(queueOperation(async () => {
      const response = await axios.put(`${apiRoot}/api/row/${row.id}/`, row)
      return response.data
    })).unwrap() // unwrap to handle rejections properly if needed
  },
)

export const listMyVocabularies = createAsyncThunk(
  'vocabularies/listVocabularies',
  async (_) => {
    const response = await axios.get(apiRoot + '/api/vocabulary/')
    return response.data
  },
)

export const setCurrentVocabulary = createAsyncThunk(
  'vocabularies/setCurrentVocabulary',
  async (id: number, { dispatch, getState }) => {
    const state = getState() as { vocabularies: VocabulariesState, preferences: { preferences: any } }
    if (id === state.vocabularies.currentVocabulary?.id) return state.vocabularies.currentVocabulary
    const response = await axios.get(apiRoot + `/api/vocabulary/${id}/`)
    if (state.preferences.preferences?.currentVocabulary !== id) {
      const preferenceResults = await dispatch(updatePreferences({ currentVocabulary: id }))
      if (!updatePreferences.fulfilled.match(preferenceResults)) {
        throw new Error('Failed to update preferences')
      }
    }
    return response.data
  },
)

export const updateCurrentVocabulary = createAsyncThunk(
  'vocabularies/updateVocabulary',
  async (updates: Partial<IVocabulary>, { getState, dispatch }) => {
    const state = getState() as { vocabularies: VocabulariesState }
    const id = updates.id || state.vocabularies.currentVocabulary?.id
    if (!id) throw new Error('Failed to update vocabulary')
    const newVocabulary = { id, ...updates }
    return dispatch(queueOperation(async () => {
      const response = await axios.patch(
        apiRoot + `/api/vocabulary/${newVocabulary.id}/`,
        newVocabulary,
      )
      if (!updates.rows) {
        delete response.data.rows
      }
      return response.data
    })).unwrap()
  },
)

interface EditPayload {
  operation: VocabularyOperation
  data: any
}

export type VocabularyOperation = 'addRow' | 'deleteRow' | 'deleteRows' | 'updateRow' | 'rearrangeRows'
export const editCurrentVocabulary = createAsyncThunk(
  'vocabularies/editCurrentVocabulary',
  async ({ operation, data }: EditPayload, { getState, dispatch }) => {
    return dispatch(queueOperation(async () => {
      const state = getState() as { vocabularies: VocabulariesState }
      const newVocabulary = { ...state.vocabularies.currentVocabulary } as IVocabulary

      if (operation === 'addRow') {
        const position = data.position as number
        const left = data.left || '' as string
        const right = data.right || '' as string
        (await axios.post(apiRoot + `/api/vocabulary/${newVocabulary.id}/modify_rows/`, {
          operation: 'insert_row',
          position: position + 1,
          left,
          right,
          vocabulary_id: newVocabulary.id,
        })).data
        return addRow(newVocabulary, position)
      }
      if (['deleteRow', 'deleteRows'].includes(operation)) {
        const positions = (operation === 'deleteRow') ? [data.position as number] : data.positions as number[]
        return (await axios.post(apiRoot + `/api/vocabulary/${newVocabulary.id}/modify_rows/`, {
          operation: 'delete_rows',
          positions,
          vocabulary_id: newVocabulary.id,
        })).data
      }
      if (operation === 'updateRow') {
        let { row, position } = data as { row: IVocabularyRow, position: number }
        if (row.id <= 0) {
          row = { ...row, id: 0 }
        }
        return (await axios.post(apiRoot + `/api/vocabulary/${newVocabulary.id}/modify_rows/`, {
          operation: 'update_row',
          vocabulary_id: newVocabulary.id,
          position,
          ...row,
        })).data
      }
      if (operation === 'rearrangeRows') {
        let { rows } = data as { rows: IVocabularyRow[] }
        const rowIds = rows.map(row => row.id)
        return (await axios.post(apiRoot + `/api/vocabulary/${newVocabulary.id}/modify_rows/`, {
          operation: 'rearrange_rows',
          vocabulary_id: newVocabulary.id,
          row_order: rowIds,
        })).data
      }
      throw new Error('Invalid operation')
    })).unwrap()
  },
)
let localRowId = -1  // always use it as nextDummyId--

export function setLocalIds(payload: ApiVocabulary): IVocabulary {
  return {
    ...payload,
    rows: payload.rows.map((row: ApiVocabularyRow) => {
      return { ...row, localId: localRowId-- }
    }),
  }
}

const addRow = (vocabulary: IVocabulary, position: number, left = '', right = '', row_id = 0) => {
  const newRow = {
    id: row_id,
    localId: localRowId--,
    left,
    right,
    left_score: 0,
    right_score: 0,
  }
  const rows = vocabulary.rows
  return [...rows.slice(0, position + 1), newRow, ...rows.slice(position + 1)]
}
const deleteRows = (vocabulary: IVocabulary, positions: number[]) => {
  return vocabulary.rows.filter((_, index) => !positions.includes(index))
}
const _updateRow = (vocabulary: IVocabulary, row: IVocabularyRow, index: number) => {
  const rows = vocabulary.rows
  return [...rows.slice(0, index), row, ...rows.slice(index + 1)]
}

export const newVocabulary = createAsyncThunk(
  'vocabularies/newVocabulary',
  async (_, { getState }) => {
    const defaultLanguage = i18n.language
    const state = getState() as { vocabularies: VocabulariesState, auth: AuthState }
    const rightLanguage = state.vocabularies.currentVocabulary?.right_language || defaultLanguage
    const leftLanguage = (
      state.vocabularies.currentVocabulary?.left_language
      || ((defaultLanguage !== rightLanguage) ? defaultLanguage :
        (defaultLanguage !== 'en') ? 'en' :
          'es')
    )
    const response = await axios.post(apiRoot + '/api/vocabulary/', {
      left_language: leftLanguage,
      right_language: rightLanguage,
      owner: state.auth.user?.id,
      rows: [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}],
    })
    return setLocalIds(response.data)
  },
)
export const deleteCurrentVocabulary = createAsyncThunk(
  'vocabularies/deleteCurrentVocabulary',
  async (_, { getState, dispatch }) => {
    const state = getState() as { vocabularies: VocabulariesState }
    if (!state.vocabularies.currentVocabulary) return
    await axios.delete(apiRoot + `/api/vocabulary/${state.vocabularies.currentVocabulary.id}/`)
    // search for the previous vocabulary in the list
    let index: number | undefined = state.vocabularies.vocabularies.indexOf(state.vocabularies.currentVocabulary) - 1
    if (index < 0) {
      index = 0
    }
    if (state.vocabularies.vocabularies.length === 1) {
      index = undefined
    }
    console.log('new index', index, index && state.vocabularies.vocabularies[index])
    const newVocabularyId = index && state.vocabularies.vocabularies[index].id
    await dispatch(updatePreferences({ currentVocabulary: newVocabularyId }))
    return state.vocabularies.currentVocabulary.id
  },
)

export const splitCurrentVocabulary = createAsyncThunk(
  'vocabularies/splitCurrentVocabulary',
  async ({ position }: { position: number }, { getState, dispatch }) => {
    const state = getState() as { vocabularies: VocabulariesState }
    if (!state.vocabularies.currentVocabulary) return
    const vocabularyId = state.vocabularies.currentVocabulary.id
    return dispatch(queueOperation(async () => {
      const result = await axios.post(apiRoot + `/api/vocabulary/${vocabularyId}/modify_rows/`, {
        operation: 'split_vocabulary',
        vocabulary_id: vocabularyId,
        position,
      })
      return { position, newVocabulary: result.data }
    })).unwrap()
  })
const splitRows = (vocabulary: IVocabulary, index: number) => {
  const rows = vocabulary.rows
  return [...rows.slice(0, index + 1)]
}
interface VocabulariesState {
  vocabularies: Array<{
    id: number
    title: string
    left_language: string
    right_language: string
    created: Date
  }>
  currentVocabulary: IVocabulary | null
  lastFocusedRow: number
  lastFocusedColumn: 'left' | 'right'
  saving: boolean
  savingError: string
  highlightedRows: number[]
}

export const getCurrentRow = createSelector(
  [(state: { vocabularies: VocabulariesState }) => state.vocabularies.currentVocabulary,
   (state: { vocabularies: VocabulariesState }) => state.vocabularies.lastFocusedRow],
  (currentVocab, lastFocusedRow) => {
    if (!currentVocab) return null
    return { index: lastFocusedRow, row: currentVocab.rows[lastFocusedRow] }
  }
)

export const getCurrentCell = createSelector(
  [(state: { vocabularies: VocabulariesState }) => state.vocabularies.lastFocusedRow,
   (state: { vocabularies: VocabulariesState }) => state.vocabularies.lastFocusedColumn],
  (rowIndex, column) => ({
    rowIndex,
    column,
  })
)

const vocabulariesSlice = createSlice({
  name: 'vocabularies',
  initialState: {
    vocabularies: [],
    currentVocabulary: null,
    saving: false,
    savingError: '',
    lastFocusedRow: 0,
    lastFocusedColumn: 'left',
    highlightedRows: [],
  } as VocabulariesState,
  reducers: {
    setFocus: (state, action: { payload: { index: number, column?: 'left' | 'right' } }) => {
      state.lastFocusedRow = action.payload.index
      if (action.payload.column) {
        state.lastFocusedColumn = action.payload.column
      }
    },
    clearHighlights: (state) => {
      state.highlightedRows = []
    },
    setHighlights: (state, action: { payload: string }) => {
      const searchText = action.payload.toLowerCase()
      if (!state.currentVocabulary || !searchText) {
        state.highlightedRows = []
        return
      }

      const matchingRows: number[] = []
      state.currentVocabulary.rows.forEach((row, index) => {
        const leftText = row.left?.toLowerCase() || ''
        const rightText = row.right?.toLowerCase() || ''
        if (leftText.includes(searchText) || rightText.includes(searchText)) {
          matchingRows.push(index)
        }
      })

      state.highlightedRows = matchingRows
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateRow.fulfilled, (state, action) => {
        const row = action.payload
        const vocab = state.currentVocabulary
        if (!vocab) return
        const rows = vocab.rows.map((r) => (r.id === row.id ? { ...r, ...row } : r))
        state.currentVocabulary = { ...vocab, rows }
        state.highlightedRows = [] // Clear highlights on any edit
      })
      .addCase(listMyVocabularies.fulfilled, (state, action) => {
        state.vocabularies = action.payload
      })
      .addCase(setCurrentVocabulary.fulfilled, (state, action) => {
        console.log('setCurrentVocabulary', action.payload)
        state.currentVocabulary = setLocalIds(action.payload)
        state.highlightedRows = [] // Clear highlights when switching vocabulary
      })
      .addCase(updateCurrentVocabulary.pending, (state) => {
        state.saving = true
        state.savingError = ''
        state.highlightedRows = [] // Clear highlights on any edit
      })
      .addCase(updateCurrentVocabulary.fulfilled, (state, action) => {
        state.saving = false
        state.savingError = ''
        const vocab = (state.vocabularies.find(
            (v) => v.id === action.payload.id) as IVocabulary
        )
        vocab.title = action.payload.title
        vocab.left_language = action.payload.left_language
        vocab.right_language = action.payload.right_language
        if (action.payload.rows && state.currentVocabulary) {
          // Preserve localIds for existing rows
          // noinspection UnnecessaryLocalVariableJS
          const updatedRows = action.payload.rows.map((newRow: IVocabularyRow) => {
            const existingRow = state.currentVocabulary?.rows.find(r => r.id === newRow.id)
            return {
              ...newRow,
              localId: existingRow?.localId || localRowId--,
            }
          })
          vocab.rows = updatedRows
        }
        state.currentVocabulary = { ...state.currentVocabulary, ...vocab }
      })
      .addCase(updateCurrentVocabulary.rejected, (state, action) => {
        state.saving = false
        state.savingError = action.error.message?.toString() || 'unknown error'
      })
      .addCase(editCurrentVocabulary.pending, (state, action) => {
        state.saving = true
        state.savingError = ''
        state.highlightedRows = [] // Clear highlights on any edit
        if (!state.currentVocabulary) return
        const { operation, data } = action.meta.arg
        const newVocabulary = { ...state.currentVocabulary }
        if (operation === 'addRow') {
          const { left, right, row_id } = data as { left: string, right: string, row_id: number }
          state.currentVocabulary = {
            ...state.currentVocabulary,
            rows: addRow(newVocabulary, data.position, left, right, row_id),
          }
        } else if (operation === 'deleteRow') {
          state.lastFocusedRow = Math.max(0, state.lastFocusedRow - 1)
          state.currentVocabulary = { ...state.currentVocabulary, rows: deleteRows(newVocabulary, [data.position]) }
        } else if (operation === 'deleteRows') {
          state.currentVocabulary = { ...state.currentVocabulary, rows: deleteRows(newVocabulary, data.positions) }
        } else if (operation === 'updateRow') {
          state.currentVocabulary = {
            ...state.currentVocabulary,
            rows: _updateRow(newVocabulary, data.row, data.position),
          }
        } else if (operation === 'rearrangeRows') {
          const { rows } = data as { rows: IVocabularyRow[] }
          state.currentVocabulary = { ...state.currentVocabulary, rows }
        }
      })
      .addCase(editCurrentVocabulary.fulfilled, (state, action) => {
        const { operation, data } = action.meta.arg
        if (!state.currentVocabulary) return
        if (operation == 'updateRow') {
          console.log('payload', action.payload, data)
          state.currentVocabulary.rows[data.position].id = action.payload.row_id
        }
        state.saving = false
      })
      .addCase(editCurrentVocabulary.rejected, (state, action) => {
        state.saving = false
        state.savingError = action.error.message?.toString() || 'unknown error'
      })
      .addCase(newVocabulary.fulfilled, (state, action) => {
        const { id, title, left_language, right_language, created } = action.payload
        state.vocabularies = [{ id, title, left_language, right_language, created }, ...state.vocabularies]
        state.currentVocabulary = action.payload
      })
      .addCase(deleteCurrentVocabulary.fulfilled, (state, action) => {
        state.vocabularies = state.vocabularies.filter((v) => v.id !== action.payload)
        state.currentVocabulary = null
      })
      .addCase(splitCurrentVocabulary.pending, (state) => {
        state.saving = true
      })
      .addCase(splitCurrentVocabulary.fulfilled, (state, action) => {
        state.saving = false
        const { position } = action.meta.arg
        if (!state.currentVocabulary) return
        state.currentVocabulary = { ...state.currentVocabulary, rows: splitRows(state.currentVocabulary, position) }
        if (!action.payload) return
        state.vocabularies = [action.payload.newVocabulary, ...state.vocabularies]
      })
  },
})
export const { setFocus, setHighlights, clearHighlights } = vocabulariesSlice.actions
export default vocabulariesSlice.reducer
